import { useBooleanFlagValue } from '@openfeature/react-sdk';
import type { ChatAgentMcpOptions, ChatAgentMcpState } from '@/lib/chat-mcp/chat-mcp-bindings';
import {
  useAgentAssignedMcpIds,
  useAgentMcpStatus,
  useChatMcpAuth,
  type AgentMcpItem,
} from '../../model';
import { McpHeadersModal } from '../McpHeadersModal/McpHeadersModal';
import { ChatMcpServers } from './ChatMcpServers';

/**
 * Implementation of the `@/lib/chat-mcp` seam — wired into the chat composer at app boot. Resolves
 * the agent's assigned catalog MCPs to live per-user status and produces the composer UI slots.
 * Owns the `user_managed_mcp_servers` flag so the chat feature never references it.
 */
export const useChatAgentMcp = (
  agentId: string | undefined,
  { onCloseMenu }: ChatAgentMcpOptions,
): ChatAgentMcpState => {
  const enabled = useBooleanFlagValue('user_managed_mcp_servers', false);
  const { mcpServerIds } = useAgentAssignedMcpIds(enabled ? agentId : undefined);
  const { items, needsAttention } = useAgentMcpStatus(mcpServerIds);
  const mcpAuth = useChatMcpAuth();

  // Credential flows open a modal (rendered at composer root) and dismiss the menu; a no_auth
  // install happens in place (no modal), so the menu stays open and the row updates live.
  const handleAuth = (item: AgentMcpItem) => {
    if (mcpAuth.openAuth(item)) onCloseMenu();
  };

  const hasMcps = items.length > 0;

  return {
    hasMcps,
    needsAttention,
    connectorsSlot: hasMcps ? (
      <ChatMcpServers
        items={items}
        needsAttention={needsAttention}
        onAuth={handleAuth}
        onRetry={mcpAuth.retry}
      />
    ) : null,
    modalSlot: enabled ? <McpHeadersModal {...mcpAuth.modalProps} /> : null,
  };
};
